// The /settings Mail card's data plane — architecture/MAIL.md §3.4.
//
// `mail.accounts` is the MEMBER surface: list / get / update / remove, ACL'd
// per row, never a token. `remove` is the DISCONNECT — the server tombstones
// the row (status 'disabled') and purges the local mirror behind it, so it
// asks first (disconnectMailAccount below says what goes).
//
// A row the viewer cannot reach answers 404, never 403, so ids cannot be
// probed (§5.11). A 404 from update/remove therefore means "not visible to
// you", not "already gone" — it is surfaced as the server's own message.
import { getSDK } from '@symbo-ls/workspace-shared/functions/sdk.js'
import { mailSameWorkspaceWriter, mailWorkspaceId } from './sameWorkspaceWriter.js'
import { withMailNavUnread } from './mailUtils.js'

// The full account rows (folders, sendAs, signature, settings) behind the
// /settings card. The setup gate's own `accounts` array carries the summary
// only, so the card reads this the first time it renders with accounts.
export const loadMailAccounts = async function (opts) {
  const root = (opts && opts.root) || (this && this.state && this.state.root)
  if (!root) return null
  const write = mailSameWorkspaceWriter(root)
  const workspaceId = mailWorkspaceId(root)
  try {
    const rows = await getSDK().execute('mail.accounts', 'list', workspaceId ? { workspaceId } : {})
    const list = Array.isArray(rows) ? rows : Array.isArray(rows && rows.data) ? rows.data : []
    // The account set is HALF the badge sum, and this read is the write that
    // can shrink it: a revoked shared-inbox grant drops the row here while the
    // stream's stale count entry survives. Re-derive `navUnreadCounts.mail` in
    // the same patch so the header dot goes out with the grant (§3.6).
    write(withMailNavUnread(root, { mailAccounts: list }))
    return list
  } catch (err) {
    // `id: null` on purpose — the failure belongs to the LIST, not to a row.
    // The card renders it at card level (SettingsCard `CardError`); the row
    // renderer matches `err.id === s.id` and would have shown nothing at all,
    // leaving a 5xx as an empty card with no words in it.
    write({ mailSettingsError: { id: null, message: (err && err.message) || '' } })
    return null
  }
}

// One account's unsaved edit. Held per id so two accounts on the same card
// never share a draft, and so a failed save leaves the person's own text on
// screen instead of reverting it under them.
export const setMailAccountDraft = function (id, patch) {
  const root = this && this.state && this.state.root
  if (!root || !id) return
  const drafts = root.mailSettingsDraft || {}
  root.update({
    mailSettingsDraft: { ...drafts, [id]: { ...(drafts[id] || {}), ...(patch || {}) } }
  })
}

// PATCH /accounts/:id — the server allowlist is displayName / signature /
// defaultSendAs / settings. Anything else is refused with 400, so the card
// sends only what the person edited.
export const saveMailAccountSettings = async function (id) {
  const root = this && this.state && this.state.root
  if (!root || !id || root.mailSettingsBusy) return false
  const draft = (root.mailSettingsDraft || {})[id]
  if (!draft || !Object.keys(draft).length) return false
  const payload = {}
  if (typeof draft.signature === 'string') payload.signature = { text: draft.signature }
  if (typeof draft.defaultSendAs === 'string' && draft.defaultSendAs) {
    payload.defaultSendAs = draft.defaultSendAs
  }
  if (typeof draft.displayName === 'string') payload.displayName = draft.displayName
  if (!Object.keys(payload).length) return false
  const write = mailSameWorkspaceWriter(root)
  const workspaceId = mailWorkspaceId(root)
  root.update({ mailSettingsBusy: { id, op: 'save' }, mailSettingsError: null })
  try {
    await getSDK().execute('mail.accounts', 'update', {
      id,
      payload,
      ...(workspaceId ? { workspaceId } : {})
    })
    const drafts = { ...(root.mailSettingsDraft || {}) }
    delete drafts[id]
    write({ mailSettingsBusy: null, mailSettingsDraft: drafts })
    await this.call('loadMailAccounts')
    this.call('openNotification', {
      type: 'success',
      title: this.call('polyglot', 'mail.settings.saved')
    })
    return true
  } catch (err) {
    write({
      mailSettingsBusy: null,
      mailSettingsError: { id, message: (err && err.message) || '' }
    })
    return false
  }
}

// DELETE /accounts/:id — the disconnect. It asks first (CRUD + confirmation
// standard, rule 4: a disconnect is a sensitive action). The server
// tombstones the row AND queues the purge of this mailbox's local mirror
// (MailSyncService requestPurge): messages, bodies, drafts, the outbox and
// stored attachments go. A real mailbox keeps its mail at the provider and
// can be connected again, but the drafts and scheduled sends saved here do
// not come back; a SIMULATED mailbox is nothing but that mirror, so for it
// everything goes for good. The question names which, from the row itself.
// Nothing is written before the person confirms.
export const disconnectMailAccount = function (id) {
  const root = this && this.state && this.state.root
  if (!root || !id || root.mailSettingsBusy) return Promise.resolve(false)
  const row = (root.mailAccounts || []).find((a) => a && a.id === id) || {}
  const address = row.address || ''
  const t = (key) => this.call('polyglot', key)
  return new Promise((resolve) => {
    this.call('confirmDialog', {
      title: t(
        row.simulated ? 'mail.confirm.disconnectDemo.title' : 'mail.confirm.disconnect.title'
      ).replace('{address}', () => address),
      message: t(
        row.simulated ? 'mail.confirm.disconnectDemo.message' : 'mail.confirm.disconnect.message'
      ),
      confirmText: t(row.simulated ? 'common.action.delete' : 'mail.settings.action.disconnect'),
      cancelText: t('common.action.cancel'),
      confirmTheme: 'primary',
      icon: null,
      onConfirm: () => resolve(_disconnectMailAccountNow.call(this, root, id)),
      onCancel: () => resolve(false)
    })
  })
}

const _disconnectMailAccountNow = async function (root, id) {
  if (root.mailSettingsBusy) return false
  const write = mailSameWorkspaceWriter(root)
  const workspaceId = mailWorkspaceId(root)
  root.update({ mailSettingsBusy: { id, op: 'remove' }, mailSettingsError: null })
  try {
    await getSDK().execute('mail.accounts', 'remove', {
      id,
      ...(workspaceId ? { workspaceId } : {})
    })
    write({ mailSettingsBusy: null })
    await this.call('loadMailAccounts')
    await this.call('loadMailSetup', { force: true })
    this.call('openNotification', {
      type: 'success',
      title: this.call('polyglot', 'mail.settings.disconnected')
    })
    return true
  } catch (err) {
    write({
      mailSettingsBusy: null,
      mailSettingsError: { id, message: (err && err.message) || '' }
    })
    return false
  }
}

// [Reconnect] — a re-auth IS the personal OAuth start over again, so it goes
// through the one connect entry point (functions/setup.js `startMailConnect`),
// which today reports that the provider route lands with
// MAIL-SERVER-GOOGLE-PROVIDER-1. One code path, so the day that route exists
// the row button follows it without a second edit.
//
// ⚠️ THE ANSWER MUST LAND WHERE THE FINGER DID. `startMailConnect` writes
// `mailSetupNotice`, which only MailSetupPanel renders — and this card
// UNMOUNTS that panel in state `ready`. So a person with one live mailbox and
// one that needs re-authorising pressed [Reconnect] and got a silent no-op:
// the reply existed, in a box that was not on their screen. The toast is the
// row's own channel and is visible on every surface, so this call site raises
// one itself rather than trusting a panel it may have hidden.
export const reconnectMailAccount = async function (id) {
  const root = this && this.state && this.state.root
  if (!root || !id) return false
  const account = (root.mailAccounts || []).find((a) => a && a.id === id)
  const started = await this.call('startMailConnect', (account && account.provider) || '')
  if (started !== true) {
    const notice = root.mailSetupNotice || {}
    this.call('openNotification', {
      type: 'info',
      title: this.call('polyglot', 'mail.settings.action.reconnect'),
      message: this.call('polyglot', notice.key || 'mail.setup.connect.pending')
    })
  }
  return started
}
